import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Money, Notice, Panel, Table, type ColumnDef } from '../design/index.js'
import { useApi, type WorkListRow, type WorkListSummary } from '../app/api.js'
import { navigate, paths } from '../app/router.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDate } from '../framework/format.js'

/**
 * Work lists (docs/11 §Setup → Work List).
 *
 * What the system is asking the business to do, as opposed to what it is
 * asking one person to do — that is the Tasks page. Each list leads with its
 * count, says what clears it, and can be switched off by an administrator when
 * a company does not work that way.
 */
export const WorkListsPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const lists = useQuery({ queryKey: ['worklists', api.companyId], queryFn: () => api.workLists() })

  const setEnabled = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => api.setWorkListEnabled(key, enabled),
    onSuccess: (next) => {
      client.setQueryData(['worklists', api.companyId], next)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (lists.isLoading) return <div className="page"><Loading /></div>
  if (lists.error) return <div className="page"><ErrorBlock error={lists.error} /></div>

  const rows = lists.data ?? []
  const waiting = rows.filter((w) => w.enabled && w.count > 0)

  return (
    <div className="page">
      <h1 className="page-title">Work lists</h1>
      <p className="page-subtitle">
        What the business has left half done: approved and not acted on, late, or waiting for somebody.
      </p>

      {error ? <div style={{ margin: '18px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      {waiting.length === 0 ? (
        <Empty
          title="Nothing is waiting"
          body="Every order has been sent, every receipt invoiced, and nothing is overdue."
        />
      ) : null}

      {rows.map((list) => (
        <WorkList
          key={list.key}
          companyId={companyId}
          list={list}
          open={open === list.key}
          onToggleOpen={() => setOpen(open === list.key ? null : list.key)}
          onEnabledChange={(enabled) => setEnabled.mutate({ key: list.key, enabled })}
        />
      ))}
    </div>
  )
}

const WorkList = ({
  companyId, list, open, onToggleOpen, onEnabledChange,
}: {
  companyId: string
  list: WorkListSummary
  open: boolean
  onToggleOpen: () => void
  onEnabledChange: (enabled: boolean) => void
}) => {
  const api = useApi()
  const client = useQueryClient()

  const rows = useQuery({
    queryKey: ['worklist', api.companyId, list.key],
    queryFn: () => api.workListRows(list.key),
    enabled: open && list.enabled,
  })

  /**
   * The one action a row offers, whichever it is.
   *
   * The catalogue names it (`markSent`, `setPurchasePlan`); the list clears
   * itself because the work was done, not because a row was ticked off.
   */
  const act = useMutation({
    mutationFn: (row: WorkListRow) =>
      list.action?.key === 'setPurchasePlan'
        ? api.setPurchasePlan(row.documentId)
        : api.markSent(row.documentId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['worklist', api.companyId, list.key] })
      void client.invalidateQueries({ queryKey: ['worklists', api.companyId] })
    },
  })

  const columns: ColumnDef<WorkListRow>[] = [
    { key: 'documentNo', header: 'Document no', width: 170,
      render: (row) => <span className="docno">{row.documentNo ?? '—'}</span> },
    { key: 'documentDate', header: 'Date', width: 130,
      render: (row) => <span className="num">{formatDate(row.documentDate)}</span> },
    { key: 'partyName', header: 'Party', render: (row) => row.partyName ?? '—' },
    { key: 'projectName', header: 'Project', render: (row) => row.projectName ?? '—' },
    { key: 'detail', header: 'Why it is here', render: (row) => row.detail ?? '' },
    { key: 'waitingDays', header: 'Waiting', width: 120, align: 'right',
      render: (row) => (
        <span className="num" style={row.waitingDays > 14 ? { color: 'var(--bad)', fontWeight: 600 } : undefined}>
          {row.waitingDays === 0 ? 'today' : `${row.waitingDays} day${row.waitingDays === 1 ? '' : 's'}`}
        </span>
      ) },
    { key: 'total', header: 'Value', width: 150, align: 'right',
      render: (row) => <Money value={row.total} /> },
    ...(list.action
      ? [{
          key: 'action', header: '', width: 150,
          render: (row: WorkListRow) => (
            <Button
              kind="ghost"
              disabled={act.isPending}
              onClick={(e) => {
                e?.stopPropagation?.()
                act.mutate(row)
              }}
            >
              {list.action?.label}
            </Button>
          ),
        }]
      : []),
  ]

  return (
    <Panel
      title={list.label}
      aside={list.enabled ? `${list.count} waiting` : 'Not in use'}
    >
      <p className="worklist__blurb">{list.blurb}</p>
      <p className="worklist__cleared">Cleared by: {list.clearedBy}</p>

      <div style={{ display: 'flex', gap: 12, margin: '16px 0' }}>
        <Button kind="ghost" disabled={!list.enabled || list.count === 0} onClick={onToggleOpen}>
          {open ? 'Hide them' : `Show the ${list.count}`}
        </Button>
        <Button kind="ghost" onClick={() => onEnabledChange(!list.enabled)}>
          {list.enabled ? 'Stop using this list' : 'Start using this list'}
        </Button>
      </div>

      {open && list.enabled ? (
        rows.isLoading ? <Loading />
          : rows.error ? <ErrorBlock error={rows.error} />
          : (rows.data ?? []).length === 0 ? (
            <Empty title="Nothing on this list" body={list.clearedBy} />
          ) : (
            <Table
              columns={columns}
              rows={[...(rows.data ?? [])]}
              getKey={(row) => row.documentId}
              onRowClick={(row) => navigate(paths.document(companyId, row.route, row.documentId))}
            />
          )
      ) : null}
    </Panel>
  )
}
